import type { interfaccia_amministro, interfaccia_archivioplus, interfaccia_notificoonline, Profile } from '@amministro-io-packages/auth-interfaces';
import { compare } from '@/lib/utils';
import { capitalize } from '@/lib/strings';
import { makeBinded } from '@/proxies';
import {
  appInterfaceAmministroValues,
  AppInterfaceArchivioplus,
  appInterfaceArchivioplusValues,
  AppInterfaceNotificoonline,
  appInterfaceNotificoonlineValues,
} from '@amministro-io-packages/administration-interfaces';
import { CONFLUENCE_PAGES } from '@/vos/helper';

export type AppInterface = Profile['interfaccia_amministroio'] | Profile['interfaccia_archivioplus'] | Profile['interfaccia_notificoonline'] | 'ADMINISTRATION';

export type AppListItem = {
  label: string | null | undefined;
  link: string | null | undefined;
  product: 'amministroio' | 'archivio' | 'notifico' | 'admin' | 'documenti_postali';
  value: boolean;
  app: AppInterface;
  locked: boolean;
  confluenceRef?: { id: string; wiki: string };
};

export type AppList = Array<AppListItem>;

export class BindingService {
  /**
   * Un profilo di tipo SYS non dispone di interfacce; le applicazioni a cui ha accesso
   * sono hard-coded lato client (amministro.io, admin.amministro.io)
   */
  private _appsFromSysProfile(): AppList {
    return [
      {
        label: this.nameFromApp(env.VUE_APP_ADMINISTRATION_ID as AppInterface),
        link: this.urlFromApp(env.VUE_APP_ADMINISTRATION_ID as AppInterface),
        product: 'admin',
        value: true,
        app: env.VUE_APP_ADMINISTRATION_ID as AppInterface,
        locked: false,
      },
    ];
  }

  /**
   * Un profilo di tipo SPL non dispone di interfacce; le applicazioni a cui ha accesso
   * sono hard-coded lato client (amministro.io, archivio.plus)
   */
  private _appsFromSplProfile(): AppList {
    return [
      {
        // AMMINISTROIO
        label: this.nameFromApp(env.VUE_APP_AMMINISTROIO_ID as AppInterface),
        link: this.urlFromApp(env.VUE_APP_AMMINISTROIO_ID as AppInterface),
        product: 'amministroio',
        value: true,
        app: env.VUE_APP_AMMINISTROIO_ID as AppInterface,
        locked: false,
      },
      {
        // ARCHIVIOPLUS
        label: this.nameFromApp(env.VUE_APP_ARCHIVIOPLUS_ID as AppInterface),
        link: this.urlFromApp(env.VUE_APP_ARCHIVIOPLUS_ID as AppInterface),
        product: 'archivio',
        value: true,
        app: env.VUE_APP_ARCHIVIOPLUS_ID as AppInterface,
        locked: false,
      },
    ];
  }

  private _appsFromAdpProfile(): AppList {
    return [
      {
        label: this.nameFromApp(env.VUE_APP_DOCUMENTI_POSTALI_ID as AppInterface),
        link: this.urlFromApp(env.VUE_APP_DOCUMENTI_POSTALI_ID as AppInterface),
        product: 'documenti_postali',
        value: true,
        app: env.VUE_APP_DOCUMENTI_POSTALI_ID as AppInterface,
        locked: false,
      },
    ];
  }

  /**
   * Dato un'url prova ad associarlo ad un'app.
   * @param href - l'url di riferimento
   */
  appFromUrl(href: string | null): AppInterface {
    if (href === null) return href;

    try {
      const { origin } = new URL(href);

      const envkey = Object.keys(env).find((key) => env[key] === origin) || '';

      return (/VUE_APP_(.*?)_URL/s.exec(envkey)?.[1] || null) as AppInterface;
    }
    catch {
      // TODO: notify error
      return null;
    }
  }

  /**
   * Data un'app ritorna l'url ad essa associata (se l'app esiste)
   * @param app - l'app da cui estrarre l'url
   */
  urlFromApp(app: AppInterface): string | null {
    if (env[`VUE_APP_${app}_ID`]) {
      return env[`VUE_APP_${app}_URL`] as string;
    }
    else {
      return null;
    }
  }

  /**
   * Data un'app ritorna l'icona ad essa associata (se l'app esiste)
   * @param app - l'app da cui estrarre l'icon
   */
  iconFromApp(app: AppInterface): string | null {
    if (env[`VUE_APP_${app}_ID`]) {
      return env[`VUE_APP_${app}_LOGO_MINI`] as string;
    }
    else {
      return null;
    }
  }

  /**
   * Data un'app ritorna il nome ad essa associata
   * @param app
   */
  nameFromApp(app: AppInterface): string | null {
    if (env[`VUE_APP_${app}_ID`]) {
      return env[`VUE_APP_${app}_DISPLAY_NAME`] as string;
    }
    else {
      return null;
    }
  }

  /**
   * Dato un profilo, ritorna la lista di tutte le app alle quali potrebbe accedere.
   * @param profile - il profilo dal quale estrarre le app
   */
  appsFromProfile(profile: Profile): AppList {
    const { role } = profile;

    const clean = (apps: AppList) => {
      return (
        apps
          .filter((v) => v.value && v.label)
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          .map((v) => ({ ...v, label: capitalize(v.label!) }))
          .sort(compare<AppListItem>('asc')('product'))
      );
    };

    switch (profile.company_type) {
      case 'sys_suppliers':
        return clean(this._appsFromSplProfile());
      case 'sys_systemAdmins':
        return clean(this._appsFromSysProfile());
      case 'adp_admins':
        return clean(this._appsFromAdpProfile());
      case 'sys_organizations':
      case 'org_contacts': {
        const { moduleAmministroio, moduleNotificoOnline, moduleArchivioPlus } = profile.moduli;

        const { interfaccia_amministroio, interfaccia_notificoonline, interfaccia_archivioplus } = profile;

        return clean([
          {
            app: interfaccia_amministroio,
            label: this.nameFromApp(interfaccia_amministroio),
            product: 'amministroio',
            link: this.urlFromApp(interfaccia_amministroio),
            value: true,
            locked: !moduleAmministroio,
          },
          {
            app: interfaccia_notificoonline,
            label: this.nameFromApp(interfaccia_notificoonline),
            product: 'notifico',
            link: this.urlFromApp(interfaccia_notificoonline),
            value: (profile.company_type === 'org_contacts' && moduleNotificoOnline) || profile.company_type === 'sys_organizations',
            locked: !moduleNotificoOnline,
            confluenceRef: Object.values(AppInterfaceNotificoonline).includes(interfaccia_notificoonline as AppInterfaceNotificoonline)
              ? CONFLUENCE_PAGES[interfaccia_notificoonline as AppInterfaceNotificoonline]
              : undefined,
          },
          {
            app: interfaccia_archivioplus,
            label: this.nameFromApp(interfaccia_archivioplus),
            product: 'archivio',
            link: this.urlFromApp(interfaccia_archivioplus),
            value: (profile.company_type === 'org_contacts' && moduleArchivioPlus) || profile.company_type === 'sys_organizations',
            locked: !moduleArchivioPlus,
            confluenceRef: interfaccia_archivioplus === AppInterfaceArchivioplus.TEAPOT ? CONFLUENCE_PAGES.TEAPOT : undefined,
          },
          {
            app: env.VUE_APP_ADMINISTRATION_ID as AppInterface,
            label: this.nameFromApp(env.VUE_APP_ADMINISTRATION_ID as AppInterface),
            product: 'admin',
            link: this.urlFromApp(env.VUE_APP_ADMINISTRATION_ID as AppInterface),
            value: role.startsWith('sys'),
            locked: false,
          },
        ]);
      }
    }
  }

  isAmministroInterface(value: string): value is interfaccia_amministro {
    return (appInterfaceAmministroValues as string[]).includes(value);
  }

  isArchivioInterface(value: string): value is interfaccia_archivioplus {
    return (appInterfaceArchivioplusValues as string[]).includes(value);
  }

  isNotificoInterface(value: string): value is interfaccia_notificoonline {
    return (appInterfaceNotificoonlineValues as string[]).includes(value);
  }

  appFromAppInterface(appInterface: AppInterface) {
    if (!appInterface) return null;

    if (this.isAmministroInterface(appInterface)) return 'amministroio';

    if (this.isArchivioInterface(appInterface)) return 'archivio';

    if (this.isNotificoInterface(appInterface)) return 'notifico';

    if (appInterface === 'ADMINISTRATION') return 'admin';

    return null;
  }
}

export const bindingService = makeBinded(BindingService);
